import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import { DashboardConfigurationInterface } from '../../models/DashboardConfiguration';
import { CloneStructure } from '../../models/template';
import { DashboardConfigurationService } from './service';
import {
  CardDetailsI,
  ChartConfigI,
  DataGridI,
  FormConfigI,
  IFormDetails,
  PivotPlaceholderI,
  TaskI,
  WorkflowI,
} from './types';
import agent from '../../api/agent';

export const defaultDashboardConfState = {
  loader: {
    isCloning: false,
    isSettingSave: false,
  },
  dashboardLevelDetails: [],
  card: {
    categories: [],
    data: {},
  },
  workflow: {
    details: {
      asset: {
        name: '',
        assetId: '',
      },
      element: {
        name: '',
        elementId: '',
      },
      template: {
        name: '',
        templateId: '',
      },
    },
    assetOptions: [],
    elementOptions: [],
    templateOptions: [],
    isAssetLoading: false,
    isElementLoading: false,
    isTemplateLoading: false,
  },
  task: {
    taskId: uuid(),
  },
  dataGrid: {
    masterId: '',
    masterName: '',
    data: [],
  },
  form: {
    masterId: '',
    masterName: '',
    data: [],
  },
  chart: {
    chartName: '',
    chartType: '',
  },
  productionAndPlanning: {
    needChart: false,
  },
  pivotPlaceholder: {
    pivotId: '',
    pivotName: '',
  },
  tableWithForm: {
    formMasterId: '',
    formMasterName: '',
    formMasterElement: '',
    formFlexBasis: '',
    formLinkName: '',
    tableMasterId: '',
    tableMasterName: '',
    tableMasterElement: '',
    tableFlexBasis: '',
    tableLinkName: '',
    formMetadataOptions: [],
    tableMetadataOptions: [],
    formOptions: [],
    tableOptions: [],
  },
};

export default class DashboardConfigurationStore {
  loader: {
    isCloning: boolean;
    isSettingSave: boolean;
  } = {
    isCloning: false,
    isSettingSave: false,
  };
  dashboardLevelDetails: {
    level: number;
    parentlevelId: string;
    selectedId: string;
    data: DashboardConfigurationInterface[];
  }[] = [];
  card: CardDetailsI = {
    categories: [],
    data: {
      style: 'style 1',
    },
  };
  workflow: WorkflowI = {
    details: {
      asset: {
        name: '',
        assetId: '',
      },
      element: {
        name: '',
        elementId: '',
      },
      template: {
        name: '',
        templateId: '',
      },
      masterName: '',
      masterOptions: [],
    },
    assetOptions: [],
    elementOptions: [],
    templateOptions: [],
    isAssetLoading: false,
    isElementLoading: false,
    isTemplateLoading: false,
  };
  task: TaskI = {
    taskId: uuid(),
  };
  dataGrid: DataGridI = {
    masterId: '',
    masterName: '',
    masterElement: '[]',
    automaticPdf: false,
    data: [],
    metaData: [],
    isMaster: true,
    assetType: '',
    elementType: '',
  };
  form: FormConfigI = {
    masterId: '',
    masterName: '',
    masterElement: '[]',
    automaticPdf: false,
    data: [],
    metaData: [],
    isMaster: true,
  };
  chart: ChartConfigI = {
    chartType: '',
    chartName: '',
  };
  pivotPlaceholder: PivotPlaceholderI = {
    pivotId: '',
    pivotName: '',
  };
  productionAndPlanning: {
    needChart: boolean;
  } = {
    needChart: false,
  };
  selectedFormLevel: number | null = null;
  isSettingLoading: boolean = false;
  deleteModalDetails: {
    id: string;
    type: string;
    index: number;
    isDeleted: boolean;
    open: boolean;
    isDeleteLoading: boolean;
  } = {
    id: '',
    type: '',
    index: 0,
    isDeleted: false,
    open: false,
    isDeleteLoading: false,
  };
  tableWithForm: {
    formMasterId: string;
    formMasterName: string;
    formMasterElement: string;
    formFlexBasis: string;
    formLinkName: string;
    tableMasterId: string;
    tableMasterName: string;
    tableMasterElement: string;
    tableFlexBasis: string;
    tableLinkName: string;
    formMetadataOptions: IFormDetails[];
    tableMetadataOptions: IFormDetails[];
    formOptions: {
      key: string;
      value: string;
      text: string;
    }[];
    tableOptions: {
      key: string;
      value: string;
      text: string;
    }[];
  } = {
    formMasterId: '',
    formMasterName: '',
    formMasterElement: '[]',
    formFlexBasis: '',
    formLinkName: '',
    tableMasterId: '',
    tableMasterName: '',
    tableMasterElement: '[]',
    tableFlexBasis: '',
    tableLinkName: '',
    formMetadataOptions: [],
    tableMetadataOptions: [],
    formOptions: [],
    tableOptions: [],
  };

  constructor() {
    makeAutoObservable(this);
  }

  getLevelDetails = async (parentlevelId?: string, key?: number) => {
    try {
      const result = await DashboardConfigurationService.DashboardLevelGet();
      runInAction(() => {
        if (result?.data) {
          this.dashboardLevelDetails[key || 0] = {
            level: key || 0,
            parentlevelId: parentlevelId || '',
            selectedId: '',
            data: result?.data,
          };
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  createDashboardAddnew = async (payload: {
    levelId: string;
    levelIndex: number;
    name: string;
    customerName: string;
    creatorName: string;
    dateTime: string;
    templateLevelId?: string;
  }) => {
    try {
      await DashboardConfigurationService.DashboardLevelPost(payload);
      runInAction(() => {
        toast.success('Add new successfully');
      });
    } catch (error) {
      console.log(error);
    }
  };

  deleteLevelItem = async (id: string, name: string) => {
    try {
      await DashboardConfigurationService.DashboardLevelDelete(id);
      runInAction(() => {
        let uppercase = name?.charAt(0)?.toLocaleLowerCase() || '';
        toast.success(`${uppercase + name.substring(1)} Deleted Successfully.`);
        this.deleteModalDetails = {
          id: '',
          type: '',
          index: 0,
          isDeleted: false,
          open: false,
          isDeleteLoading: false,
        };
      });
    } catch (error) {
      this.deleteModalDetails = {
        id: '',
        type: '',
        index: 0,
        isDeleted: false,
        open: false,
        isDeleteLoading: false,
      };
      toast.error(`Something went wrong.`);
      console.log(error);
    }
  };

  editLevelItem = async (levelId: string, name: any) => {
    try {
      await DashboardConfigurationService.DashboardLevelEdit(levelId, name);
    } catch (error) {}
  };

  ReArrangeOrder = async (id: string, parentLevelId: string, index: number) => {
    try {
      await DashboardConfigurationService.DashboardLevelIndexChange(
        id,
        parentLevelId,
        index,
      );
      runInAction(() => {
        toast.success('Swapped Successfully');
      });
    } catch (error) {
      console.log(error);
    }
  };

  afterDeleteFetchLevelDetails = async (parentlevelId: string, key: number) => {
    try {
      let result = await DashboardConfigurationService.DashboardNextLevel({
        parentlevelId: parentlevelId,
      });
      runInAction(() => {
        if (result?.data) {
          this.dashboardLevelDetails[key] = {
            level: key,
            parentlevelId,
            selectedId: '',
            data: result?.data,
          };
        }
      });
      // this.loader.isSettingSave = false;
    } catch (error) {
      this.dashboardLevelDetails[key + 1] = {
        level: key + 1,
        parentlevelId,
        selectedId: '',
        data: [],
      };
      for (let i = key + 2; i < this.dashboardLevelDetails.length; i++) {
        delete this.dashboardLevelDetails[i];
      }
      // this.loader.isSettingSave = false;
      toast.success('Something went wrong.');
      console.log(error);
    }
  };

  // ----------------- Next level --------------------- //
  nextLevelGET = async (
    parentlevelId: string,
    level: number,
    duplicateDashboardLevelDetails: {
      level: number;
      parentlevelId: string;
      selectedId: string;
      data: DashboardConfigurationInterface[];
    }[],
  ) => {
    // this.loader.isSettingSave = true;
    try {
      let result = await DashboardConfigurationService.DashboardNextLevel({
        parentlevelId: parentlevelId,
      });
      runInAction(() => {
        if (result?.data) {
          duplicateDashboardLevelDetails[level + 1] = {
            level: level + 1,
            parentlevelId,
            selectedId: '',
            data: result?.data,
          };
          this.dashboardLevelDetails = duplicateDashboardLevelDetails;
        }
      });
      // this.loader.isSettingSave = false;
    } catch (error) {
      this.dashboardLevelDetails[level + 1] = {
        level: level + 1,
        parentlevelId,
        selectedId: '',
        data: [],
      };
      // this.loader.isSettingSave = false;
      toast.error('Something went wrong.');
      console.log(error);
    }
  };

  // --------------- Clone ------------------ //
  cloneItem = async (clone: CloneStructure) => {
    this.loader.isCloning = true;
    try {
      let result = await DashboardConfigurationService.cloneItemPOST(clone);
      let { data } = result || {};
      runInAction(() => {
        if (data) {
          // this.loadTemplateById(data.id);
        }
      });
      toast.success('Task Cloned Succesfully.');
      this.loader.isCloning = false;
    } catch (error) {
      this.loader.isCloning = false;
      console.log(error);
    }
  };

  // ----------------- setting PUT --------------------- //
  dashboardSetting = async (payload: any, indexOfLevel: number) => {
    this.loader.isSettingSave = true;
    try {
      await DashboardConfigurationService.DashboardSettingPUT(payload);
      runInAction(() => {
        let findIndex = this.dashboardLevelDetails?.[
          indexOfLevel
        ]?.data?.findIndex((d) => d.levelId === payload.levelId);
        if (this.dashboardLevelDetails?.[indexOfLevel]?.data?.[findIndex]) {
          this.dashboardLevelDetails[indexOfLevel].data[findIndex] = {
            ...this.dashboardLevelDetails[indexOfLevel].data[findIndex],
            type: payload.type,
            icon: payload.icon,
            flexBasis: payload.flexBasis,
          };
        }
        toast.success(
          `${
            payload.type.charAt(0).toUpperCase() + payload.type.slice(1)
          } saved successfully.`,
        );
      });
      this.loader.isSettingSave = false;
      return 'succeed';
    } catch (error) {
      this.loader.isSettingSave = false;
      toast.error('Something went wrong.');
      console.log(error);
      return error;
    }
  };

  // ----------------- setting GET --------------------- //
  getDashboardSettingDetails = async (levelId: string) => {
    try {
      let result = await DashboardConfigurationService.DashboardSettingGET({
        levelId,
      });
      return result?.data;
    } catch (error) {
      toast.error('Pls Try Again !');
      return error;
    }
  };

  getMasterMetaDataBasedOnID = async (payload: {
    masterId: string;
    ids: string[];
  }) => {
    try {
      let result =
        await agent.newMasterReport.getMasterMetaDataBasedOnID(payload);
      return result?.data;
    } catch (error) {
      toast.error('Pls Try Again !');
      return error;
    }
  };

  assignUserMasterUpdate = async (
    assetId: string,
    userIdsPayload: string[],
  ) => {
    try {
      await agent.newMasterReport.assignUserMasterUpdate(
        assetId,
        userIdsPayload,
      );
      toast.success('User(s) assigned or removed successfully.');
    } catch {
      toast.error('Something went wrong in assigning users.');
    }
  };

  GETMasterMetaData = async (masterId: string) => {
    try {
      // let result = await DashboardConfigurationService.GETMasterMetaData({
      //   masterId,
      // });
      let result = await agent.newMasterReport.getMetadataList({
        masterId,
      });
      return result?.data;
    } catch (error) {
      toast.error('Pls Try Again !');
      return error;
    }
  };

  // ----------------- master table GET --------------------- //
  getMasterDetails = async () => {
    try {
      let result = await DashboardConfigurationService.MastertableDetailsGET();
      return result?.data;
    } catch (error) {
      toast.error('Pls Try Again !');
      console.log(error);
      return error;
    }
  };
}
